import React, { useState } from 'react';
import { Play, Trophy, Users, Beer, Footprints, ChevronRight, ChevronDown, Camera, Flag, LogOut, Zap, FileText, HeartPulse, Check } from 'lucide-react';
import theme from '../themes';
import { activityMeta } from '../utils/redemption';
import { RAP_SHEET_OPTIN_KEY, isRapSheetEnabled } from '../hooks/useMorningWorkout';
import { isHealthSupported, ensureHealthPermission, healthSourceLabel } from '../native/health';

const HEALTH_CONNECTED_KEY = 'bender_health_connected';

const Dashboard = ({ user, stats, squadMembers, squadId, onOpenSquad, onStart, onOpenOverlay, onLogout, redemption }) => {

  // Count active squad members
  const activeSquadCount = squadMembers.filter(m => m.location).length;

  // Rap Sheet is opt-in: no morning verdicts unless the user wants them.
  const [rapSheetOn, setRapSheetOn] = useState(isRapSheetEnabled());
  const toggleRapSheet = () => {
    const next = !rapSheetOn;
    try { localStorage.setItem(RAP_SHEET_OPTIN_KEY, next ? '1' : '0'); } catch { /* ignore */ }
    setRapSheetOn(next);
    redemption?.recheck?.(); // apply immediately (shows/hides the morning card)
  };

  // Apple Health / Health Connect must be identifiable in the UI on its own —
  // App Review rejected 1.1.2 (10) under 2.5.1 because the only path to the
  // HealthKit feature was behind the (default-off) Rap Sheet toggle, so a
  // reviewer could never find it. This row renders whenever the platform
  // supports health data, regardless of that toggle.
  const healthSupported = isHealthSupported();
  const [healthConnected, setHealthConnected] = useState(() => {
    try { return localStorage.getItem(HEALTH_CONNECTED_KEY) === '1'; } catch { return false; }
  });
  const [healthBusy, setHealthBusy] = useState(false);
  const [healthOpen, setHealthOpen] = useState(false);
  const [standingsOpen, setStandingsOpen] = useState(false);
  const connectHealth = async () => {
    setHealthBusy(true);
    try {
      const ok = await ensureHealthPermission();
      try { localStorage.setItem(HEALTH_CONNECTED_KEY, ok ? '1' : '0'); } catch { /* ignore */ }
      setHealthConnected(ok);
      if (ok) redemption?.recheck?.();
      else alert(`${healthSourceLabel()} isn't available on this device.`);
    } finally {
      setHealthBusy(false);
    }
  };

  return (
    // overflow-y-auto + an inner my-auto wrapper instead of justify-center:
    // justify-center on a fixed-height flex column clips overflow off the TOP
    // (unreachable even with scrolling) — which is exactly how the logo went
    // missing when the Health row made the stack taller than short screens.
    // transform-gpu + no mount animation: mounting this screen mid-keyboard-
    // dismissal hit a WKWebView compositing bug — the flattened layer (background
    // and all non-transformed children) failed to paint for ~10s while the
    // skew-transformed buttons, on their own GPU layers, painted fine. Giving the
    // root its own layer and skipping the transient fade avoids the race.
    <div className="absolute inset-0 z-[2000] bg-black flex flex-col items-center overflow-y-auto no-scrollbar px-6 pt-[calc(env(safe-area-inset-top,0px)+1.25rem)] pb-[calc(env(safe-area-inset-bottom,0px)+1.25rem)] transform-gpu">
     <div className="my-auto w-full flex flex-col items-center">

      {/* --- BRANDING --- */}
      <div className="w-full max-w-sm text-center mb-5 flex flex-col items-center">
          {/* New Bender Racing Logo */}
          <img
              src={theme.branding.logo}
              alt={theme.branding.logoAlt}
              className="h-28 w-auto mb-4 drop-shadow-2xl opacity-95"
          />
          
          <div className="transform -skew-x-12 bg-brand-pink px-6 py-2 border-l-4 border-brand-teal shadow-[4px_4px_0px_var(--accent-2)] inline-block">
              <div className="transform skew-x-12">
                  <h1 className="text-3xl font-black italic text-white uppercase tracking-tighter leading-none">
                      {theme.branding.title.replace(theme.branding.titleAccent, '').trim()} <span className="text-black">{theme.branding.titleAccent}</span>
                  </h1>
              </div>
          </div>
          
          <p className="text-zinc-500 text-[10px] font-black tracking-[0.4em] uppercase mt-3">
              {theme.branding.tagline}
          </p>
      </div>

      {/* --- WEEKLY STATS CARD — collapsed to one row (score inline) so the
             Dashboard fits one screen; the chevron expands the full breakdown. --- */}
      <div className="w-full max-w-sm bg-zinc-900 border-2 border-zinc-800 mb-4 relative overflow-hidden transform -skew-x-12 shadow-lg">
          <div className="absolute top-0 right-0 w-32 h-32 bg-brand-teal/10 rounded-full blur-3xl -mr-16 -mt-16 pointer-events-none"></div>

          <button onClick={() => setStandingsOpen(o => !o)} className="w-full px-5 py-3.5">
              <div className="transform skew-x-12 flex justify-between items-center">
                  <h2 className="text-[10px] font-black text-zinc-400 uppercase tracking-widest flex items-center gap-2">
                      <Trophy size={14} className="text-brand-teal" /> Season Standings
                  </h2>
                  <div className="flex items-center gap-2.5">
                      <span className="text-base font-black text-white italic leading-none">
                          {stats.score || 0} <span className="text-[9px] text-brand-pink tracking-widest not-italic">PTS</span>
                      </span>
                      <ChevronDown size={14} className={`text-zinc-500 transition-transform ${standingsOpen ? 'rotate-180' : ''}`} />
                  </div>
              </div>
          </button>

          {standingsOpen && (
            <div className="px-5 pb-4 transform skew-x-12">
              <div className="flex justify-between items-end">
                  <div>
                      <div className="text-5xl font-black text-white italic tracking-tighter leading-none drop-shadow-md">
                          {stats.score || 0}
                      </div>
                      <div className="text-[10px] font-black text-brand-pink uppercase mt-1 tracking-widest">
                          Current Rank Points
                      </div>
                  </div>

                  <div className="text-right space-y-1.5">
                      <div className="flex items-center justify-end gap-2 text-white">
                          <span className="text-sm font-black">{stats.drinks || 0}</span>
                          <Beer size={14} className="text-brand-teal" />
                      </div>
                      <div className="flex items-center justify-end gap-2 text-white">
                          <span className="text-sm font-black">{stats.steps || 0}</span>
                          <Footprints size={14} className="text-brand-pink" />
                      </div>
                  </div>
              </div>
              <div className="mt-3 text-[9px] font-black text-zinc-500 uppercase tracking-widest">
                  Resets Tuesday
              </div>
            </div>
          )}
      </div>

      {/* --- SQUAD STATUS --- */}
      <div className="w-full max-w-sm mb-5">
          <button 
            onClick={onOpenSquad}
            className="w-full bg-zinc-900 border-2 border-zinc-800 active:scale-95 transition-transform hover:border-brand-teal group transform -skew-x-12 shadow-lg"
          >
              <div className="flex items-center justify-between px-4 py-3 transform skew-x-12">
                  <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 flex items-center justify-center border-2 ${squadId ? 'bg-white border-white text-black' : 'bg-black border-zinc-700 text-zinc-400 group-hover:text-brand-teal group-hover:border-brand-teal transition-colors'}`}>
                          <Users size={18} />
                      </div>
                      <div className="flex flex-col items-start">
                          <span className="text-xs font-black text-white uppercase tracking-widest">
                            {squadId ? `${theme.labels.squadLabel} Status` : `Assemble ${theme.labels.squadLabel}`}
                          </span>
                          <span className={`text-[9px] font-black uppercase tracking-widest mt-0.5 ${squadId ? 'text-brand-teal' : 'text-zinc-500'}`}>
                              {squadId 
                                  ? (activeSquadCount > 0 ? theme.labels.squadCountFmt.replace('{n}', activeSquadCount) : "No one is active yet")
                                  : "Tap to Create or Join"
                              }
                          </span>
                      </div>
                  </div>

                  {squadId ? (
                    activeSquadCount > 0 ? (
                        <div className="flex -space-x-2">
                            {squadMembers.filter(m => m.location).slice(0,3).map(m => (
                                <div key={m.id} className="w-7 h-7 rounded-full bg-brand-teal border-2 border-black flex items-center justify-center text-[9px] font-black text-black shadow-md">
                                    {m.name?.charAt(0)}
                                </div>
                            ))}
                        </div>
                    ) : (
                        <ChevronRight size={18} className="text-zinc-600" />
                    )
                  ) : (
                    <ChevronRight size={18} className="text-brand-teal animate-pulse" />
                  )}
              </div>
          </button>
      </div>

      {/* --- REDEMPTION DETECTED (hybrid athlete) --- */}
      {redemption?.status === 'found' && redemption.workout && (
        <div className="w-full max-w-sm mb-4 bg-zinc-900 border-2 border-brand-teal transform -skew-x-12 shadow-[4px_4px_0px_var(--accent-1)] animate-in slide-in-from-bottom-2">
          <div className="transform skew-x-12 px-4 py-3">
            <div className="flex items-center gap-2 mb-1">
              <Zap size={14} className="text-brand-teal" />
              <span className="text-[10px] font-black text-brand-teal uppercase tracking-widest">Redemption detected</span>
            </div>
            <p className="text-xs font-bold text-white leading-snug">
              {activityMeta(redemption.workout.type).emoji} We found a{' '}
              {redemption.workout.distanceKm ? `${redemption.workout.distanceKm.toFixed(1)} km ` : `${Math.round(redemption.workout.durationMin)} min `}
              {activityMeta(redemption.workout.type).label.toLowerCase()} this morning. Pair it with last night's{' '}
              {redemption.lastSession?.drinks || 0} drinks?
            </p>
            <div className="flex gap-2 mt-3">
              <button onClick={redemption.onPair} className="flex-1 bg-brand-teal text-black font-black text-[10px] py-2.5 uppercase tracking-widest active:scale-95 transition-transform">
                Pair it
              </button>
              <button onClick={redemption.onLogWorkout} className="px-3 text-zinc-400 hover:text-white font-black text-[10px] uppercase tracking-widest">
                Log other
              </button>
              <button onClick={redemption.onDismiss} className="px-3 text-zinc-500 hover:text-white font-black text-[10px] uppercase tracking-widest">
                Not now
              </button>
            </div>
          </div>
        </div>
      )}

      {/* No health data (web / Android today) or nothing found → log it by hand,
          or own the IN DEBT verdict. Same card on every platform. */}
      {(redemption?.status === 'none' || redemption?.status === 'unsupported') && (
        <div className="w-full max-w-sm mb-4 bg-zinc-900 border-2 border-zinc-700 transform -skew-x-12 shadow-[4px_4px_0px_#27272a]">
          <div className="transform skew-x-12 px-4 py-3">
            <div className="flex items-center gap-2 mb-1">
              <Zap size={14} className="text-brand-pink" />
              <span className="text-[10px] font-black text-brand-pink uppercase tracking-widest">Redemption window open</span>
            </div>
            <p className="text-[11px] font-bold text-zinc-300 leading-snug">
              {redemption.lastSession?.drinks || 0} drinks on the books from last night.
              {redemption.status === 'none' ? ' No workout found in Health. ' : ' '}
              Did you pay it back this morning?
            </p>
            <div className="flex gap-2 mt-3">
              <button onClick={redemption.onLogWorkout} className="flex-1 bg-brand-pink text-white font-black text-[10px] py-2.5 uppercase tracking-widest active:scale-95 transition-transform">
                Log a workout
              </button>
              <button onClick={redemption.onPairNothing} className="px-3 bg-zinc-800 text-white font-black text-[10px] uppercase tracking-widest border border-zinc-700">
                Nope · stamp it
              </button>
              <button onClick={redemption.onDismiss} className="px-2 text-zinc-600 hover:text-white font-black text-[10px] uppercase tracking-widest">
                ✕
              </button>
            </div>
          </div>
        </div>
      )}

      {/* --- ACTION BUTTONS --- */}
      <div className="w-full max-w-sm space-y-4">
          <button 
              onClick={onStart} 
              className="w-full bg-brand-pink hover:bg-red-600 text-white font-black text-lg py-5 border-2 border-black shadow-[4px_4px_0px_var(--accent-2)] flex items-center justify-center gap-3 uppercase tracking-widest active:translate-y-1 active:shadow-none transition-all transform -skew-x-12"
          >
              <div className="transform skew-x-12 flex items-center gap-3">
                  <Flag size={24} className="fill-white" /> {theme.labels.startSession}
              </div>
          </button>

          <button 
              onClick={onOpenOverlay}
              className="w-full bg-black border-2 border-zinc-700 text-zinc-300 font-black text-xs py-4 flex items-center justify-center gap-2 uppercase tracking-widest active:translate-y-1 transition-all hover:bg-zinc-900 hover:text-white transform -skew-x-12 shadow-[4px_4px_0px_#27272a] hover:border-zinc-500"
          >
              <div className="transform skew-x-12 flex items-center gap-2">
                  <Camera size={16} /> Generate Quick Recap
              </div>
          </button>

          {redemption?.rapSheet && (
            <button
                onClick={redemption.onOpenRapSheet}
                className="w-full bg-black border-2 border-zinc-700 text-zinc-300 font-black text-xs py-4 flex items-center justify-center gap-2 uppercase tracking-widest active:translate-y-1 transition-all hover:bg-zinc-900 hover:text-white transform -skew-x-12 shadow-[4px_4px_0px_#27272a] hover:border-zinc-500"
            >
                <div className="transform skew-x-12 flex items-center gap-2">
                    <FileText size={16} /> View Rap Sheet
                    <span className="text-[9px] px-1.5 py-0.5 border" style={{ color: redemption.rapSheet.redemption.key === 'in_debt' ? '#f4626e' : redemption.rapSheet.redemption.key === 'certified_hybrid' ? '#e6d027' : '#78d5c3', borderColor: 'currentColor' }}>
                      {redemption.rapSheet.redemption.label}
                    </span>
                </div>
            </button>
          )}

          {/* Rap Sheet opt-in — morning workout-vs-drinks verdicts only for those
              who asked for them. Off = never prompted, never judged. */}
          <button
              onClick={toggleRapSheet}
              className="w-full bg-black border border-zinc-800 px-4 py-3 flex items-center justify-between transition-colors hover:border-zinc-600"
          >
              <div className="flex items-center gap-2 text-left">
                  <Zap size={14} className={rapSheetOn ? 'text-brand-teal' : 'text-zinc-600'} />
                  <div>
                      <div className={`font-black text-[10px] uppercase tracking-widest ${rapSheetOn ? 'text-white' : 'text-zinc-400'}`}>Morning Rap Sheet</div>
                      <div className="text-[9px] font-bold text-zinc-600">Pair a workout with last night · get the verdict</div>
                  </div>
              </div>
              <div className={`w-10 h-5 rounded-full relative transition-colors ${rapSheetOn ? 'bg-brand-teal' : 'bg-zinc-700'}`}>
                  <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${rapSheetOn ? 'left-5' : 'left-0.5'}`} />
              </div>
          </button>

          {/* Apple Health / Health Connect — labeled row always visible on native
              (App Review 2.5.1: HealthKit use must be identifiable in the UI, not
              hidden behind the default-off Rap Sheet opt-in). Collapsed by default
              to keep the Dashboard short; the chevron expands the explainer +
              Connect action. The always-visible NAME is what satisfies 2.5.1. */}
          {healthSupported && (
            <div className="w-full bg-black border border-zinc-800">
              <button
                onClick={() => setHealthOpen(o => !o)}
                className="w-full px-4 py-3 flex items-center justify-between transition-colors hover:border-zinc-600"
              >
                <div className="flex items-center gap-2">
                  <HeartPulse size={14} className={healthConnected ? 'text-brand-teal' : 'text-zinc-500'} />
                  <span className="font-black text-[10px] uppercase tracking-widest text-white">{healthSourceLabel()}</span>
                  {healthConnected && (
                    <span className="flex items-center gap-1 text-brand-teal font-black text-[9px] uppercase tracking-widest">
                      <Check size={11} /> Connected
                    </span>
                  )}
                </div>
                <ChevronDown size={14} className={`text-zinc-500 transition-transform ${healthOpen ? 'rotate-180' : ''}`} />
              </button>
              {healthOpen && (
                <div className="px-4 pb-3 flex items-center justify-between gap-3">
                  <p className="text-[9px] font-bold text-zinc-600 leading-snug text-left">
                    Reads your morning workouts from {healthSourceLabel()} to build the Rap Sheet. Read-only — nothing is ever written back.
                  </p>
                  {!healthConnected && (
                    <button
                      onClick={connectHealth}
                      disabled={healthBusy}
                      className="shrink-0 bg-zinc-900 border border-zinc-700 text-white font-black text-[9px] uppercase tracking-widest px-3 py-2 hover:border-zinc-500 active:scale-95 transition-all disabled:opacity-50"
                    >
                      {healthBusy ? 'Connecting…' : 'Connect'}
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Log out / switch account — deliberately low-key so it never competes
              with GO LIVE, but reachable so staff can get back to the login gate. */}
          {onLogout && (
            <button
                onClick={onLogout}
                className="w-full text-zinc-600 hover:text-zinc-300 font-black text-[10px] py-2 flex items-center justify-center gap-2 uppercase tracking-widest transition-colors"
            >
                <LogOut size={12} /> Log out / Switch account
            </button>
          )}
      </div>
     </div>

    </div>
  );
};

export default Dashboard;
